/**
 * One day in the family weekly order view — a card that navigates to the
 * full-page day view on tap. Shows the date pill + weekday, an
 * "Order by …" deadline chip, a chevron, and a per-child summary of meals
 * already ordered for the day (avatar + name + item thumbnails). EVERY child
 * appears, grouped by campus (each campus keeps its own menu teaser); the
 * campus name is captioned only when the family spans more than one. Editing
 * happens on the full-page day view, not in place.
 */

import { useQueries } from '@tanstack/react-query'
import { ChevronRight } from 'lucide-react'
import { type ReactElement } from 'react'
import { Link } from 'react-router'

import { type MenuWeekPreviewDay, menuWeekPreviewQueryOptions } from '@/api/orders'
import type { FamilyAlreadyOrdered } from '@/lib/family-orders/already-ordered'
import {
  campusSectionClosed,
  dayIsNoService,
  noServiceCardState,
  sharedBandForLocation,
} from '@/lib/family-orders/no-service-day'
import { dateFromKey, mondayOf } from '@/lib/school-orders/calendar'

import { FamilyCollapsedSummary } from './collapsed-summary'
import { acceptsParentOrders, type FamilyOrderLocation } from './columns'
import type { DayBadge } from './day-badges'
import { DayStatusBadge } from './day-status-badge'

export interface FamilyDayCardProps {
  locations: readonly FamilyOrderLocation[]
  dateKey: string
  alreadyOrdered: FamilyAlreadyOrdered
  /** Per-day status pill (Order by / Edit by / Complete / Not ordered). */
  badge: DayBadge
  /** Full-page day-view URL the card navigates to. */
  href: string
}

export function FamilyDayCard({ locations, dateKey, alreadyOrdered, badge, href }: FamilyDayCardProps): ReactElement {
  const date = dateFromKey(dateKey)
  const monthAbbr = date.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' }).toUpperCase()
  const dayNum = date.getUTCDate()
  const weekday = date.toLocaleDateString(undefined, { weekday: 'long', timeZone: 'UTC' })

  // One week-preview fetch per campus, keyed (location, weekStart) — the five
  // day cards of a week share the same cached responses. Fanned out here rather
  // than inside each campus section so the CARD can also answer "is the school
  // closed today", which is a roll-up across every campus.
  const previews = useQueries({
    queries: locations.map((location) =>
      menuWeekPreviewQueryOptions({
        locationId: location.id,
        weekStart: mondayOf(dateKey),
        gradeRangeCode: sharedBandForLocation(location),
        // Band-less student (fail-closed 2026-07 gate): they see no menus, so
        // skip the fetch.
        enabled: sharedBandForLocation(location) !== null,
      }),
    ),
  })
  const previewByLocationId = new Map<string, MenuWeekPreviewDay | null>(
    locations.map((location, i) => [location.id, previews[i]?.data?.days.find((day) => day.date === dateKey) ?? null]),
  )
  // The school isn't serving today at ANY of the family's campuses — a blocked
  // date, a weekday switched off on the service days, or an out-of-session day.
  // Unresolved campuses count as "unknown", never as closed, so a slow fetch
  // can't grey out a day the family could order on.
  const noService = dayIsNoService(locations.map((location) => previewByLocationId.get(location.id) ?? null))
  // A day switched off after a family ordered on it keeps its real badge and
  // stays navigable — greying it out would strand their receipt. A view-only
  // campus section (school ordering only) keeps the card open on every served
  // day: its menu is what the parent comes to see.
  const hasViewOnlyCampus = locations.some((location) => !acceptsParentOrders(location))
  const { showNoService, disabled } = noServiceCardState({ noService, status: badge.status, hasViewOnlyCampus })

  const content = (
    <>
      <div className="flex w-full items-center justify-between gap-3 px-4 pt-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-md bg-muted leading-none">
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{monthAbbr}</span>
            <span className="text-base font-semibold text-foreground">{dayNum}</span>
          </span>
          <span className="text-xl font-medium text-foreground">{weekday}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* A no-service day carries no deadline — an "Order by" countdown on a
              day the school never serves is the confusion this replaces. */}
          <DayStatusBadge
            tone={showNoService ? 'neutral' : badge.tone}
            label={showNoService ? 'No service' : badge.label}
          />
          {disabled ? null : <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" strokeWidth={1.75} />}
        </div>
      </div>
      <div className="flex flex-col gap-9 px-4 pb-4">
        {locations.map((location) => (
          <DayCardCampusSection
            key={location.id}
            location={location}
            dateKey={dateKey}
            alreadyOrdered={alreadyOrdered}
            closed={campusSectionClosed({
              viewOnly: !acceptsParentOrders(location),
              showNoService,
              status: badge.status,
            })}
            noService={showNoService}
            viewOnly={!acceptsParentOrders(location)}
            menuPreview={previewByLocationId.get(location.id) ?? null}
            showCampusName={locations.length > 1}
          />
        ))}
      </div>
    </>
  )

  if (disabled) {
    return (
      <div
        aria-disabled="true"
        className="flex flex-col gap-7 overflow-hidden rounded-xl border bg-card text-left opacity-60">
        {content}
      </div>
    )
  }

  return (
    <Link
      to={href}
      className="flex flex-col gap-7 overflow-hidden rounded-xl border bg-card text-left transition-colors hover:bg-muted/30 focus:outline-none focus-visible:outline-none">
      {content}
    </Link>
  )
}

/**
 * One campus's slice of the day card: its children + its own "On the menu"
 * teaser. When the family spans multiple campuses each child row carries a
 * pin + campus caption under the name. The teaser is passed in — the card owns
 * the fetch so it can also roll the days up into a no-service verdict.
 */
function DayCardCampusSection({
  location,
  dateKey,
  alreadyOrdered,
  closed,
  noService,
  viewOnly,
  menuPreview,
  showCampusName,
}: {
  location: FamilyOrderLocation
  dateKey: string
  alreadyOrdered: FamilyAlreadyOrdered
  closed: boolean
  noService: boolean
  /** School ordering only: the section shows the menu, never ordering copy. */
  viewOnly: boolean
  menuPreview: MenuWeekPreviewDay | null
  showCampusName: boolean
}): ReactElement {
  return (
    <FamilyCollapsedSummary
      dateKey={dateKey}
      locationId={location.id}
      columns={location.children}
      alreadyOrdered={alreadyOrdered}
      closed={closed}
      noService={noService}
      viewOnly={viewOnly}
      menuPreview={menuPreview}
      campusName={showCampusName ? location.name : null}
    />
  )
}
